import { useEffect, useState } from 'react'
import './CustomConfirmModal.css'

type ModalType = 'confirm' | 'danger' | 'success' | 'warning' | 'info'

interface CustomConfirmModalProps {
    isOpen: boolean
    title: string
    message: string
    onConfirm: (inputValue?: string) => void
    onCancel?: () => void
    confirmText?: string
    cancelText?: string
    type?: ModalType
    showInput?: boolean
    inputPlaceholder?: string
    showCancel?: boolean
}

export default function CustomConfirmModal({
    isOpen,
    title,
    message,
    onConfirm,
    onCancel,
    confirmText = 'Confirmar',
    cancelText = 'Cancelar',
    type = 'confirm',
    showInput = false,
    inputPlaceholder = 'Escribe un mensaje opcional...',
    showCancel = true,
    'data-testid': testId,
}: Readonly<CustomConfirmModalProps & { 'data-testid'?: string }>) {
    const [inputValue, setInputValue] = useState('')

    // Reset input when modal opens (render-phase update)
    const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
    if (isOpen && !prevIsOpen) {
        setInputValue('')
        setPrevIsOpen(isOpen)
    } else if (!isOpen && prevIsOpen) {
        setPrevIsOpen(isOpen)
    }

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }
        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [isOpen])

    // Handle escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen && showCancel && onCancel) {
                onCancel()
            }
        }
        window.addEventListener('keydown', handleEscape)
        return () => window.removeEventListener('keydown', handleEscape)
    }, [isOpen, onCancel, showCancel])

    if (!isOpen) return null

    const getIcon = () => {
        switch (type) {
            case 'danger':
                return '⚠️'
            case 'success':
                return '✅'
            case 'warning':
                return '⚠️'
            case 'info':
                return 'ℹ️'
            default:
                return '❓'
        }
    }

    const handleOverlayClick = () => {
        if (showCancel && onCancel) {
            onCancel()
        }
    }

    return (
        <div className="custom-confirm-overlay" onClick={handleOverlayClick} role="presentation" data-testid={testId || "confirm-modal-active"}>
            <div
                className="custom-confirm-modal animate-scale-in"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="confirm-title"
                data-testid="confirm-modal-container"
            >
                <div className="custom-confirm-header">
                    <div className={`confirm-icon-circle ${type}`}>{getIcon()}</div>
                    <h3 id="confirm-title" className="confirm-title" data-testid="confirm-modal-title">
                        {title}
                    </h3>
                </div>
                <div className="custom-confirm-body">
                    <p className="confirm-message" data-testid="confirm-modal-message">{message}</p>
                    {showInput && (
                        <textarea
                            className="confirm-input"
                            placeholder={inputPlaceholder}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            rows={3}
                            autoFocus
                            data-testid="confirm-modal-input"
                        />
                    )}
                </div>
                <div className={`custom-confirm-footer ${!showCancel ? 'single-button' : ''}`}>
                    {showCancel && (
                        <button className="btn-secondary" onClick={onCancel} data-testid="confirm-modal-cancel-btn">
                            {cancelText}
                        </button>
                    )}
                    <button
                        className={type === 'danger' ? 'btn-danger' : 'btn-primary'}
                        onClick={() => onConfirm(inputValue)}
                        autoFocus={!showInput}
                        data-testid="confirm-modal-confirm-btn"
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    )
}
